import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ActionProposal } from '../api/types.js';
import { useActions, useDecideAction } from '../hooks/use-actions.js';
import { Button } from './button.js';
import { timeAgo } from '../utils/time-ago.js';

// « Proposed by a session » (ADR-039 step 2): a review to prepare, a deployment to analyse, a decision on one. Nothing
// runs before the operator approves it; then aidev runs it with his rights, as from its own page. What the session
// says is its own reading of what it saw: plain text, never instructions.

const DECISION = { approve: 'Approve', reject: 'Reject', set_aside: 'Set aside' } as const;

export function actionTitle(p: ActionProposal): string {
  const target = `${p.repo ?? ''} to ${p.environment ?? ''}`;
  if (p.action === 'review') return `Review ${p.pr}`;
  if (p.action === 'deployment-analysis') return `Analyse the deployment of ${target}`;
  return `${DECISION[p.decision ?? 'approve']} the deployment of ${target}`;
}

/** Where to read what the action is about: the pull request on GitHub, or the run waiting */
function targetUrl(p: ActionProposal): string | null {
  if (p.action === 'review' && p.pr) {
    const m = /^([^/]+)\/([^#]+)#(\d+)$/.exec(p.pr);
    return m ? `https://github.com/${m[1]}/${m[2]}/pull/${m[3]}` : null;
  }
  return p.repo && p.runId ? `https://github.com/${p.repo}/actions/runs/${p.runId}` : null;
}

function Pending({ serverId, p, isAdmin, focused }: { serverId: string; p: ActionProposal; isAdmin: boolean; focused: boolean }) {
  const decide = useDecideAction(serverId);
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => { if (focused) ref.current?.scrollIntoView?.({ block: 'center' }); }, [focused]);
  const url = targetUrl(p);
  const edge = p.action === 'deployment-decision' ? 'var(--color-warn)' : 'var(--color-brand)';
  return (
    <li ref={ref} id={`action-${p.id}`} className="py-3 pl-3" style={{ borderLeft: `3px solid ${edge}` }}>
      <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
        {url ? <a href={url} target="_blank" rel="noreferrer" className="hover:underline">{actionTitle(p)}</a> : actionTitle(p)}
      </p>
      <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>
        Proposed {timeAgo(p.createdAt)} by{' '}
        {p.requestedBySession
          ? <Link to={`/servers/${serverId}/sessions/${p.requestedBySession}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>a session</Link>
          : 'a session'}
        . Nothing runs before you approve it.
      </p>
      <figure className="mt-2 max-w-[72ch]">
        <figcaption className="text-xs" style={{ color: 'var(--color-text-3)' }}>Why, in the session&apos;s words</figcaption>
        <blockquote className="mt-0.5 whitespace-pre-wrap break-words text-sm" style={{ color: 'var(--color-text-2)' }}>{p.reason}</blockquote>
      </figure>
      {isAdmin && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: p.id, decision: 'approve' })}>
            {decide.isPending && decide.variables?.decision === 'approve' ? 'Running…' : 'Approve'}
          </Button>
          <Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate({ id: p.id, decision: 'reject' })}>Reject</Button>
        </div>
      )}
      {decide.isError && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not done: {(decide.error as Error).message}</p>}
    </li>
  );
}

function outcome(p: ActionProposal): { text: string; color: string } {
  if (p.status === 'failed') return { text: `Failed: ${p.error ?? 'unknown error'}`, color: 'var(--color-error)' };
  if (p.status === 'rejected') return { text: 'Rejected', color: 'var(--color-text-3)' };
  if (p.status === 'running') return { text: 'Running', color: 'var(--color-text-2)' };
  return { text: p.result ?? 'Done', color: 'var(--color-text-2)' };
}

export function ActionProposals({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data } = useActions(serverId);
  const [params] = useSearchParams();
  const [history, setHistory] = useState(false);
  const all = data ?? [];
  const pending = all.filter((p) => p.status === 'pending');
  const decided = all.filter((p) => p.status !== 'pending');
  if (!all.length) return null;
  const focus = params.get('action');
  return (
    <section aria-labelledby="actions-title" className="mb-8 min-w-0">
      <h2 id="actions-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Proposed by a session</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>
        {pending.length ? 'Approve and aidev runs it for you, as from its own page; reject and nothing happens.' : 'Nothing waits on you here.'}
      </p>
      {pending.length > 0 && (
        <ul className="mt-3 space-y-1">
          {pending.map((p) => <Pending key={p.id} serverId={serverId} p={p} isAdmin={isAdmin} focused={focus === p.id} />)}
        </ul>
      )}
      {decided.length > 0 && (
        <div className="mt-2">
          <button type="button" onClick={() => setHistory(!history)} aria-expanded={history}
            className="text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-text-3)' }}>
            {decided.length} decided{history ? '' : ': show'}
          </button>
          {history && (
            <ul className="mt-2 space-y-1.5">
              {decided.map((p) => {
                const o = outcome(p);
                return (
                  <li key={p.id} className="pl-3 text-sm" style={{ borderLeft: '3px solid var(--color-border)' }}>
                    <span style={{ color: 'var(--color-text-2)' }}>{actionTitle(p)}</span>
                    <span className="block text-xs" style={{ color: o.color }}>{o.text}{p.decidedAt ? `, ${timeAgo(p.decidedAt)}` : ''}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
